import { Button, Form, Input, Select, Space, Table, Typography, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { UpdateRollout } from '../api/go';
import type { UpdateRolloutRecord, UpdateRolloutStatus } from '../api/go';
import {
  ROLLOUT_STATUS_DRAFT,
  ROLLOUT_STATUS_GRAY,
  ROLLOUT_STATUS_OFFICIAL,
  getErrorMessage,
  nullableText,
  releaseStatusMap,
  renderOssKey,
  renderRolloutSummary,
  renderStatusTag,
  rolloutStatusOptions,
} from './shared';
import { useRolloutEditor } from './rollout_editor';

type RolloutFilterForm = {
  module_key?: string;
  rollout_statuses?: UpdateRolloutStatus[];
};

const defaultRolloutStatusFilter: UpdateRolloutStatus[] = [ROLLOUT_STATUS_DRAFT, ROLLOUT_STATUS_GRAY, ROLLOUT_STATUS_OFFICIAL];

export function RolloutTab(props: { refreshKey: number }) {
  const { refreshKey } = props;
  const [filterForm] = Form.useForm<RolloutFilterForm>();
  const [records, setRecords] = useState<UpdateRolloutRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const rolloutEditor = useRolloutEditor({
    onSaved: async () => {
      await loadRolloutList();
    },
  });

  async function loadRolloutList() {
    setLoading(true);
    try {
      const values = filterForm.getFieldsValue();
      const data = await UpdateRollout.list({
        module_key: values.module_key?.trim() || undefined,
        rollout_statuses: values.rollout_statuses,
      });
      setRecords(data.records || []);
    } catch (err) {
      notification.error({ message: '加载 rollout 失败', description: await getErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRolloutList();
  }, [refreshKey]);

  const columns: ColumnsType<UpdateRolloutRecord> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '模块 Key', dataIndex: 'module_key', width: 140 },
    {
      title: '版本',
      dataIndex: 'module_version',
      width: 180,
      render: (value: string, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{value || '-'}</Typography.Text>
          <Typography.Text type="secondary">{row.release_name || (row.release_id ? `Release #${row.release_id}` : '-')}</Typography.Text>
        </Space>
      ),
    },
    { title: '构建号', dataIndex: 'build_number', width: 150, render: nullableText },
    {
      title: 'Release 状态',
      dataIndex: 'release_status',
      width: 120,
      render: (value: UpdateRolloutRecord['release_status']) => (value ? renderStatusTag(value, releaseStatusMap) : nullableText(value)),
    },
    {
      title: 'Rollout',
      width: 220,
      render: (_, row) => renderRolloutSummary(row),
    },
    {
      title: 'OSS Key',
      dataIndex: 'url',
      ellipsis: true,
      render: (value: string | undefined, row) => (value ? renderOssKey(value, row.download_url) : nullableText(value)),
    },
    {
      title: '校验串',
      dataIndex: 'sha1',
      width: 260,
      render: (value: string | undefined) => (value ? <Typography.Text copyable={{ text: value }}>{value}</Typography.Text> : nullableText(value)),
    },
    { title: '备注', dataIndex: 'remark', width: 180, ellipsis: true, render: nullableText },
    { title: '更新时间', dataIndex: 'updated_at', width: 180 },
    {
      title: '操作',
      width: 110,
      fixed: 'right',
      render: (_, row) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => rolloutEditor.openRolloutEdit(row)}>
          编辑
        </Button>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Button icon={<ReloadOutlined />} onClick={() => loadRolloutList()} loading={loading}>
        刷新
      </Button>
      <Form<RolloutFilterForm>
        form={filterForm}
        layout="inline"
        initialValues={{
          rollout_statuses: [...defaultRolloutStatusFilter],
        }}
        onFinish={loadRolloutList}
        style={{ marginBottom: 16 }}
      >
        <Form.Item<RolloutFilterForm> label="模块 Key" name="module_key">
          <Input allowClear placeholder="例如 platform" />
        </Form.Item>
        <Form.Item<RolloutFilterForm> label="Rollout 状态" name="rollout_statuses">
          <Select
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            style={{ minWidth: 280 }}
            options={rolloutStatusOptions}
            placeholder="选择 Rollout 状态"
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              查询
            </Button>
            <Button
              onClick={() => {
                filterForm.resetFields();
                loadRolloutList();
              }}
            >
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table<UpdateRolloutRecord>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={records}
        pagination={false}
        scroll={{ x: 1620 }}
      />
      {rolloutEditor.modal}
    </Space>
  );
}
